import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { stringEquals } from '@utils/string.util';
import { MatTooltipModule } from '@angular/material/tooltip';
import { tagStyle } from '@utils/tag-color.util';

/** タグのViewModel */
export type TagViewModel = {
  /** タグ名 */
  tagName: string;
  /** タグId */
  tagId: string;
  /** メールアドレス */
  notifyEmail?: string;
  color?: string | null;
};

@Component({
  selector: 'app-tenant-setting-tag',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatTooltipModule,
  ],
  templateUrl: './tenant-setting-tag.component.html',
  styleUrl: './tenant-setting-tag.component.scss',
})
export class TenantSettingTagComponent implements AfterViewInit, OnChanges {
  public readonly tagStyle = tagStyle;
  /** タグ一覧 */
  @Input() tagList: TagViewModel[] = [];
  /** 新規登録を通知 */
  @Output() created = new EventEmitter<void>();
  /** 更新を通知 */
  @Output() updated = new EventEmitter<TagViewModel>();
  /** 削除を通知 */
  @Output() deleted = new EventEmitter<TagViewModel[]>();
  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;
  /** カラム */
  public readonly displayedColumns: string[] = [
    'checkbox',
    'tagName',
    'operation',
  ];
  /** 一覧情報 */
  public dataSource = new MatTableDataSource<TagViewModel>([]);
  /** 選択状態 */
  @Input() public selection = new SelectionModel<TagViewModel>(true, []);

  /** タグ名検索キーワード */
  public tagNameKeyword = '';

  constructor(private el: ElementRef) {}

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;

    this.dataSource.sort = this.sort;

    // フィルター設定
    this.dataSource.filterPredicate = (element: TagViewModel) => {
      let tagName = true;
      if (this.tagNameKeyword) {
        // tagNameが空でない場合のみフィルタリングを行う
        tagName = stringEquals(element.tagName, this.tagNameKeyword);
      }
      return tagName;
    };
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['tagList'] !== undefined) {
      const tagList: TagViewModel[] = changes['tagList'].currentValue;
      this.dataSource.data = tagList;
    }
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.filteredData);
  }

  /**
   * すべて選択されているか
   *
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 一括削除する
   */
  public deleteAll() {
    this.deleted.emit(this.selection.selected);
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }
  /**
   * テナントでのフィルタリングをクリアする。
   */
  public clearTagNameFilter(): void {
    // フィルタ条件をクリア
    this.tagNameKeyword = '';

    this.applyFilter();
  }
}
